import { useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { CompareResponse, CreateRatingRequest } from '@/types/compare'
import { CompareCard } from '@/components/compare/CompareCard'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface CompareCardStackProps {
  responses: CompareResponse[]
  onSubmitRating: (request: CreateRatingRequest) => Promise<void>
}

export function CompareCardStack({ responses, onSubmitRating }: CompareCardStackProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const touchStartX = useRef<number | null>(null)

  const activeResponse = responses[activeIndex]

  const goNext = () => setActiveIndex((index) => Math.min(index + 1, responses.length - 1))
  const goPrev = () => setActiveIndex((index) => Math.max(index - 1, 0))

  return (
    <div
      className="flex h-full flex-col"
      data-testid="compare-card-stack"
      onTouchStart={(event) => {
        touchStartX.current = event.changedTouches[0]?.clientX ?? null
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return
        const delta = event.changedTouches[0].clientX - touchStartX.current
        if (delta > 50) goPrev()
        if (delta < -50) goNext()
        touchStartX.current = null
      }}
    >
      <div className="mb-2 flex items-center justify-between px-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={goPrev}
          disabled={activeIndex === 0}
          aria-label="Previous model"
          data-testid="compare-swipe-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-xs text-muted-foreground" data-testid="compare-stack-indicator">
          {activeIndex + 1} / {responses.length}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={goNext}
          disabled={activeIndex >= responses.length - 1}
          aria-label="Next model"
          data-testid="compare-swipe-next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {activeResponse ? (
        <CompareCard
          key={activeResponse.modelResponseId}
          response={activeResponse}
          onSubmitRating={onSubmitRating}
          className="min-h-0 flex-1"
        />
      ) : null}

      <div className="mt-2 flex justify-center gap-1">
        {responses.map((response, index) => (
          <button
            key={response.modelResponseId}
            type="button"
            aria-label={`Show ${response.model.displayName}`}
            onClick={() => setActiveIndex(index)}
            className={cn(
              'h-2 w-2 rounded-full',
              index === activeIndex ? 'bg-primary' : 'bg-muted-foreground/40',
            )}
          />
        ))}
      </div>
    </div>
  )
}
